import { useState, useCallback } from 'react';
import { api } from '@/services/api';
import { Event, CreateEventPayload, UpdateEventPayload, RSVP } from '@/types';

export function useEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const listEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listMyEvents();
      setEvents(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getEvent = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEvent(id);
      setCurrentEvent(data);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEventBySlug = useCallback(async (slug: string, password?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEventBySlug(slug, password);
      setCurrentEvent(data);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createEvent = useCallback(async (payload: CreateEventPayload) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.createEvent(payload);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateEvent = useCallback(async (id: string, payload: UpdateEventPayload) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.updateEvent(id, payload);
      setCurrentEvent(data);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEvent = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteEvent(id);
      setEvents((prev) => prev.filter((e) => e.event_id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const listRSVPs = useCallback(async (eventId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listRSVPs(eventId);
      setRsvps(data);
      return data;
    } catch (err: any) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyPassword = useCallback(async (slug: string, password: string) => {
    try {
      const result = await api.verifyPassword(slug, password);
      return result.valid;
    } catch {
      return false;
    }
  }, []);

  const submitRSVP = useCallback(async (slug: string, data: any) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.submitRSVP(slug, data);
      return result;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    events,
    currentEvent,
    rsvps,
    loading,
    error,
    clearError,
    listEvents,
    getEvent,
    getEventBySlug,
    createEvent,
    updateEvent,
    deleteEvent,
    listRSVPs,
    verifyPassword,
    submitRSVP,
  };
}
